import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';

import {
  TeamUserService,
  TeamUserList,
  SystemRole,
  UserStatus,
  RoleOption,
  PluginAccess
} from '../../core/services/team-user.service';
import { InviteService } from '../../core/services/invite.service';
import { ToastService } from '../../shared/services/toast.service';
import { AuthService } from '../../core/services/auth.service';

import { SelectModule } from '@openng/optimus-ui/select';
import { ButtonModule } from '@openng/optimus-ui/button';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { TableModule } from '@openng/optimus-ui/table';
import { BadgeModule } from '@openng/optimus-ui/badge';
import { IconFieldModule } from '@openng/optimus-ui/iconfield';
import { InputIconModule } from '@openng/optimus-ui/inputicon';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';

import {
  getBadgeSeverity as badgeSeverityFor,
  isValidEmailFormat,
  processUsersData
} from './user-list.helpers';
import { UserInviteModalComponent } from './modals/user-invite-modal.component';
import { UserEditModalComponent } from './modals/user-edit-modal.component';
import { UserBulkInviteModalComponent, BulkInviteSubmit } from './modals/user-bulk-invite-modal.component';

interface UserFormData {
  id?: string;
  email: string;
  fullName: string;
  position: string;
  birthday?: Date;
  systemRole: SystemRole;
  pluginAccess: PluginAccess[];
}

@Component({
  selector: 'app-user-list',
  imports: [
    CommonModule,
    FormsModule,
    SelectModule,
    InputTextModule,
    ButtonModule,
    DialogModule,
    TableModule,
    BadgeModule,
    IconFieldModule,
    InputIconModule,
    TooltipModule,
    ProgressSpinnerModule,
    UserInviteModalComponent,
    UserEditModalComponent,
    UserBulkInviteModalComponent
  ],
  templateUrl: './user-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserListComponent implements OnInit {
  UserStatus = UserStatus;
  SystemRole = SystemRole;
  private readonly teamUserService = inject(TeamUserService);
  private readonly toastService = inject(ToastService);
  private readonly authService = inject(AuthService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly invitesService = inject(InviteService);

  users: TeamUserList[] = [];
  roleOptions: RoleOption[] = [];

  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showBulkInviteModal = false;
  selectedUser: TeamUserList | null = null;

  formData: UserFormData = {
    email: '',
    fullName: '',
    position: '',
    systemRole: SystemRole.Designer,
    pluginAccess: []
  };

  formErrors: {[key: string]: string} = {};
  loading = false;
  searchTerm = '';

  filters = { search: '' };
  filteredUsers: TeamUserList[] = [];
  processedUsers: any[] = [];

  // Whether current user can manage team (SuperAdmin/Admin)
  canManageTeam = false;

  ngOnInit(): void {
    const sr = this.authService.getCurrentUser()?.systemRole ?? 0;
    this.canManageTeam = sr === 1 || sr === 2;
    this.loadData();
  }

  onEmailChange(email: string): void {
    this.formData.email = email;
    this.clearEmailError();
  }

  private clearEmailError(): void {
    if (this.formErrors['email']) {
      delete this.formErrors['email'];
    }
  }

  private loadData(): void {
    this.loading = true;
    this.cdr.markForCheck(); // Trigger change detection for loading state

    Promise.all([
      this.teamUserService.getAvailableRoles().toPromise(),
      this.teamUserService.getAvailablePlugins().toPromise(),
      this.teamUserService.getUsers().toPromise()
    ]).then(([roles, _plugins, users]) => {
      this.roleOptions = roles || [];

      // All users are already in the list, including those with Pending status
      this.users = users || [];

      // Apply search filter and process data
      this.applySearchFilter();
      this.loading = false;
      this.cdr.markForCheck(); // Trigger change detection for loaded data
    }).catch(error => {
      console.error('Error loading data:', error);
      this.toastService.error('Failed to load data');
      this.loading = false;
      this.cdr.markForCheck(); // Trigger change detection for error state
    });
  }

  private applySearchFilter(): void {
    if (!this.searchTerm.trim()) {
      this.filteredUsers = [...this.users];
    } else {
      const term = this.searchTerm.toLowerCase();
      this.filteredUsers = this.users.filter(user =>
        user.email.toLowerCase().includes(term) ||
        (user.fullName && user.fullName.toLowerCase().includes(term))
      );
    }
    this.filters.search = this.searchTerm;
    // Process users data only when filtered users change
    this.processedUsers = processUsersData(this.filteredUsers, (role) =>
      this.teamUserService.getRoleDisplayName(role)
    );
    this.cdr.markForCheck(); // Trigger change detection for filtered data
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.applySearchFilter();
  }

  openAddModal(): void {
    this.formData = {
      email: '',
      fullName: '',
      position: '',
      systemRole: SystemRole.Designer,
      pluginAccess: this.teamUserService.getDefaultPluginAccess()
    };
    this.formErrors = {};
    this.showAddModal = true;
    this.refreshView();
  }

  openEditModal(user: TeamUserList): void {
    if (user.status === UserStatus.Pending) {
      this.toastService.error('Cannot edit a pending invite');
      return;
    }
    this.selectedUser = user;

    this.teamUserService.getUser(user.id).subscribe((fullUser) => {
      this.formData = {
        id: fullUser.id,
        email: fullUser.email,
        fullName: fullUser.fullName || '',
        position: fullUser.position || '',
        birthday: fullUser.birthday ? new Date(fullUser.birthday) : undefined,
        systemRole: fullUser.systemRole,
        pluginAccess: [...fullUser.pluginAccess]
      };
      this.formErrors = {};
      this.showEditModal = true;
      this.refreshView();
    }, (error) => {
      console.error('Error loading user details:', error);
      this.toastService.error('Failed to load user details');
    });
  }

  openDeleteModal(user: TeamUserList): void {
    this.selectedUser = user;
    this.showDeleteModal = true;
    this.refreshView();
  }

  openBulkInviteModal(): void {
    this.showBulkInviteModal = true;
    this.refreshView();
  }

  closeModals(): void {
    this.showAddModal = false;
    this.showEditModal = false;
    this.showDeleteModal = false;
    this.showBulkInviteModal = false;
    this.selectedUser = null;
    this.formData = {
      email: '',
      fullName: '',
      position: '',
      systemRole: SystemRole.Designer,
      pluginAccess: []
    };
    this.formErrors = {};
    this.refreshView();
  }

  validateForm(): boolean {
    this.formErrors = {};
    let isValid = true;

    if (!this.formData.email?.trim()) {
      this.formErrors['email'] = 'Email is required';
      isValid = false;
    } else if (!isValidEmailFormat(this.formData.email)) {
      this.formErrors['email'] = 'Invalid email format';
      isValid = false;
    }

    if (!this.formData.systemRole) {
      this.formErrors['systemRole'] = 'Role is required';
      isValid = false;
    }

    return isValid;
  }

  saveUser(): void {
    if (!this.validateForm()) return;

    if (this.formData.id) {
      this.updateUser();
    } else {
      this.createUser();
    }
  }

  private createUser(): void {
    // Create user request - this will be converted to invite by backend
    const userRequest = {
      email: this.formData.email,
      position: this.formData.position,
      systemRole: this.formData.systemRole,
      pluginAccess: this.formData.pluginAccess || []
    };

    this.run(this.teamUserService.createUser(userRequest), 'Invitation sent successfully', 'Failed to send invitation', () => this.closeModals());
  }

  private updateUser(): void {
    if (!this.selectedUser) return;

    const updateRequest = {
      fullName: this.formData.fullName,
      position: this.formData.position,
      birthday: this.formData.birthday?.toISOString(),
      systemRole: this.formData.systemRole,
      pluginAccess: this.formData.pluginAccess
    };

    this.run(this.teamUserService.updateUser(this.selectedUser.id, updateRequest), 'User updated successfully', 'Failed to update user', () => this.closeModals());
  }

  deleteUser(): void {
    if (!this.selectedUser) return;

    this.run(this.teamUserService.deleteUser(this.selectedUser.id), 'User deleted successfully', 'Failed to delete user', () => this.closeModals());
  }

  sendBulkInvites(submission: BulkInviteSubmit): void {
    // Use emails from chips
    const emails = submission.emails.filter(email => isValidEmailFormat(email));

    if (emails.length === 0) {
      this.toastService.error('No valid email addresses found');
      return;
    }

    // Create bulk invite requests compatible with API
    const req = {
      users: emails.map(email => ({
        email,
        position: submission.position || null,
        systemRole: submission.systemRole,
        ttlDays: 7
      }))
    };

    this.run(this.invitesService.bulk(req), `Sent ${emails.length} invitations successfully`, 'Failed to send invitations', () => this.closeModals());
  }

  toggleUserStatus(user: TeamUserList, status: UserStatus): void {
    if (user.status === UserStatus.Pending) {
      this.toastService.error('Cannot change status for a pending invite');
      return;
    }
    const obs = status === UserStatus.Inactive
      ? this.teamUserService.deactivateUser(user.id)
      : this.teamUserService.activateUser(user.id);
    this.run(obs, 'User status updated', 'Failed to update status');
  }

  getFormRoleOptions(): any[] {
    return this.roleOptions.map(r => ({ value: r.value, label: r.label }));
  }

  canEditRole(): boolean {
    return this.selectedUser ? !this.isUserProtected(this.selectedUser) : true;
  }

  isUserProtected(user: TeamUserList): boolean {
    return user.isProtected || user.systemRole === SystemRole.SuperAdmin;
  }

  getBadgeSeverity(value: any): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    return badgeSeverityFor(value);
  }

  resendInvite(row: any): void {
    if (!row?.id) return;

    // For pending users, use the invite service to resend
    if (row.status === 'Pending') {
      this.run(this.invitesService.resend(row.id), 'Invite resent', 'Failed to resend invite');
    } else {
      this.toastService.error('Only pending invites can be resent');
    }
  }

  cancelInvite(row: any): void {
    if (!row?.id) return;

    // For pending users, use the invite service to cancel/delete
    if (row.status === 'Pending') {
      this.run(this.invitesService.cancel(row.id), 'Invite cancelled', 'Failed to cancel invite');
    } else {
      // For active users, use the regular user service to delete
      this.run(this.teamUserService.deleteUser(row.id), 'User deleted', 'Failed to delete user');
    }
  }

  /** Shared request flow: success toast, reload, optional close. Errors surface the API message when present. */
  private run(source: Observable<unknown>, successMessage: string, failureFallback: string, onComplete?: () => void): void {
    this.loading = true;
    source.subscribe({
      next: () => {
        this.toastService.success(successMessage);
        this.loadData();
        onComplete?.();
        this.loading = false;
      },
      error: (err) => {
        const msg = err?.error?.message || failureFallback;
        this.toastService.error(msg);
        this.loading = false;
      }
    });
  }

  /** OnPush friendly refresh so freshly toggled modal flags reach the DOM this tick. */
  private refreshView(): void {
    this.cdr.markForCheck();
    setTimeout(() => this.cdr.detectChanges(), 0);
  }
}
